import type { Application } from '~entities/application';
import {
  isShortApplicationFlow,
  isRejected,
} from '~entities/application/lib/status-checkers';

import { useOnMountEffect } from '~shared/hooks';
import errorImg from '~shared/images/error.webp';
import { nonNullableValue } from '~shared/types/non-nullable-value';
import { Button } from '~shared/ui/button';
import { Result } from '~shared/ui/result';

import { ButtonLink } from '@breeze-platform-ui/button';
import Tooltip from '@breeze-platform-ui/tooltip';
import { addDays, format } from 'date-fns';

import preapprovedImg from './images/pre-approved.webp';
import { RejectionTooltip } from './rejection-reason-tooltip';

import { rejectionTimeoutPassed } from '../../lib';

export const ApplicationStatusUI: React.FC<Application> = (application) => {
  const { applicationStatusChangedUTC } = application;
  const statusChangedDate = new Date(
    nonNullableValue(applicationStatusChangedUTC)
  );

  useOnMountEffect(() => {
    const shouldRedirectToShopifyCheckout =
      application.test && application.redirectUrl;

    if (shouldRedirectToShopifyCheckout) {
      const timerId = setTimeout(
        () => window.open(application.redirectUrl, '_self'),
        1000
      );

      return () => clearTimeout(timerId);
    }
  });

  const getContent = () => {
    if (isShortApplicationFlow(application)) {
      return (
        <Result
          title="Your application is pre-approved"
          subtitle={
            <>
              To&nbsp;finish your application, go
              to&nbsp;the&nbsp;Skyro&nbsp;app
            </>
          }
          image={<img src={preapprovedImg} width={150} height={150} alt="" />}
          footer={
            <Button href="https://link.skyro.ph/lNBg/jtjfabpb" target="_blanc">
              Open Skyro app
            </Button>
          }
        />
      );
    }

    if (isRejected(application) && rejectionTimeoutPassed(statusChangedDate)) {
      const nextApplicationTime = format(addDays(new Date(), 5), 'MMMM dd');

      return (
        <Result
          title="Your application wasn’t&nbsp;approved "
          subtitle={`You can try again after ${nextApplicationTime}`}
          image={errorImg}
          footer={
            <Tooltip
              popoverContent={<RejectionTooltip />}
              theme="dark"
              align="center"
              hideOnClickOutside
              hideOnTouchMove
              toggleOnClick
            >
              <ButtonLink size="m">Why was I not approved?</ButtonLink>
            </Tooltip>
          }
        />
      );
    }

    return (
      <Result
        title="We got your application, boss!"
        subtitle={<>Download the Skyro&nbsp;app to&nbsp;view your status.</>}
        image={<img src={preapprovedImg} width={150} height={150} alt="" />}
        footer={
          <Button href="https://link.skyro.ph/lNBg/jtjfabpb" target="_blanc">
            Install the app
          </Button>
        }
      />
    );
  };

  return <div className="pt-[min(100px,15%)]">{getContent()}</div>;
};
